import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { ListItem } from '@frameio/vapor';
import type { SectionId } from '../../../shared/types';
import {
  getQuickSearchUpsellLabel,
  QuickSearchNlpUpsellFooter,
  QuickSearchStandardUpsellFooter,
} from '../QuickSearchTabBar/QuickSearchNlpUpsellFooter';
import type { QuickSearchTabUpsellDescriptor } from './getQuickSearchTabbedAffordances';

type UpsellListItemRootProps = ComponentPropsWithoutRef<'div'>;

type CreateQuickSearchTabUpsellListItemProps = {
  descriptor: QuickSearchTabUpsellDescriptor;
  onAction: (targetSectionId: SectionId) => void;
};

function createUpsellListItemRoot(onActivate: () => void) {
  const UpsellListItemRoot = forwardRef<
    HTMLDivElement,
    UpsellListItemRootProps
  >(function QuickSearchUpsellListItemRoot(
    { onClick, onKeyDown, ...props },
    ref
  ) {
    return (
      <div
        {...props}
        ref={ref}
        onClick={(event) => {
          onClick?.(event);

          if (!event.defaultPrevented) {
            onActivate();
          }
        }}
        onKeyDown={(event) => {
          if (
            !event.repeat &&
            (event.key === 'Enter' ||
              event.key === ' ' ||
              event.key === 'Spacebar')
          ) {
            event.preventDefault();
            event.stopPropagation();
            onActivate();
            return;
          }

          onKeyDown?.(event);
        }}
      />
    );
  });

  UpsellListItemRoot.displayName = 'QuickSearchUpsellListItemRoot';

  return UpsellListItemRoot;
}

export function createQuickSearchTabUpsellListItem({
  descriptor,
  onAction,
}: CreateQuickSearchTabUpsellListItemProps) {
  const ItemRoot = createUpsellListItemRoot(() =>
    onAction(descriptor.targetSectionId)
  );

  return (
    <ListItem
      key={descriptor.key}
      as={ItemRoot}
      textValue={getQuickSearchUpsellLabel(descriptor)}
    >
      {descriptor.variant === 'ai' ? (
        <QuickSearchNlpUpsellFooter count={descriptor.count} />
      ) : (
        <QuickSearchStandardUpsellFooter count={descriptor.count} />
      )}
    </ListItem>
  );
}
